import { apiClient } from "./index";

// Get repair count statistics
export const getRepairCount = async () => {
  try {
    const response = await apiClient.get("/return-management/repairs/count/");
    
    // Handle the response structure - API returns flat object with count values
    const data = response.data || {};
    
    // Normalize the response structure to match component expectations
    // API returns: in_process_count, under_repair_count, successfully_repaired_count, lost_count
    const normalizedData = {
      in_progress: data.in_process_count || 0,
      under_repair: data.under_repair_count || 0,
      successfully_repaired: data.successfully_repaired_count || 0,
      lost: data.lost_count || 0,
    };

    return { response: normalizedData, error: null };
  } catch (error) {
    console.error("Error fetching repair count:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch repair count",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Get repairs list with filters and pagination
export const getRepairsList = async (
  search = "",
  page = 1,
  pageSize = 25,
  status = "",
  dateFilter = ""
) => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (page) params.append("page", page.toString());
    if (pageSize) params.append("page_size", pageSize.toString());
    if (status && status !== "all") params.append("status", status);
    if (dateFilter && dateFilter !== "all") params.append("date_filter", dateFilter);

    const response = await apiClient.get(
      `/return-management/repairs/?${params.toString()}`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching repairs list:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch repairs list",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Get repair by ID
export const getRepairById = async (repairId) => {
  try {
    const response = await apiClient.get(
      `/return-management/repairs/${repairId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching repair by ID:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch repair",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Update repair (PATCH)
export const updateRepair = async (repairId, repairData) => {
  try {
    const response = await apiClient.patch(
      `/return-management/repairs/${repairId}/`,
      repairData
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error updating repair:", error);
    const errorResponse = error.response?.data || {};
    
    // Extract error message from backend response
    let errorMessage = 
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to update repair";
    
    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors = errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }
    
    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Delete repair
export const deleteRepair = async (repairId) => {
  try {
    const response = await apiClient.delete(
      `/return-management/repairs/${repairId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error deleting repair:", error);
    const errorResponse = error.response?.data || {};
    
    // Extract error message from backend response
    let errorMessage = 
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to delete repair";
    
    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors = errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }
    
    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

